import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import MenuForm from "./MenuForm";
import { deleteMenu, fetchMenuFlatList, fetchMenuTree, updateMenu } from "../../../services/menuApi";
import { usePermission } from "../../../hooks/usePermission";

function flattenWithDepth(nodes = [], depth = 0, acc = []) {
  nodes.forEach((n) => {
    acc.push({ ...n, depth });
    if (Array.isArray(n.children) && n.children.length) flattenWithDepth(n.children, depth + 1, acc);
  });
  return acc;
}

export default function MenuList() {
  const canAdd = usePermission("role", "update");
  const canUpdate = usePermission("role", "update");
  const canDelete = usePermission("role", "update");

  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [parentOptions, setParentOptions] = useState([]);

  const rows = useMemo(() => flattenWithDepth(tree), [tree]);

  const loadMenus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [menuTree, flat] = await Promise.all([fetchMenuTree(), fetchMenuFlatList()]);
      setTree(menuTree);
      setParentOptions(flat);
    } catch (err) {
      setError(err.message || "Failed to load menus.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMenus();
  }, [loadMenus]);

  const openEdit = (menu) => {
    setEditingMenu(menu);
    setShowEditModal(true);
  };

  const closeEdit = () => {
    setShowEditModal(false);
    setEditingMenu(null);
  };

  const handleUpdate = async (values, { setSubmitting }) => {
    try {
      await updateMenu(editingMenu._id, {
        name: values.name,
        key: values.key,
        path: values.path,
        icon: values.icon,
        parentId: values.parentId || null,
        order: Number(values.order || 0),
        isActive: Boolean(values.isActive),
      });
      toast.success("Menu updated successfully.");
      closeEdit();
      loadMenus();
    } catch (err) {
      toast.error(err.message || "Failed to update menu.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (menu) => {
    Swal.fire({
      title: "Delete Menu?",
      text: `Delete "${menu.name}" and disable its children?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await deleteMenu(menu._id);
        toast.success("Menu deleted successfully.");
        loadMenus();
      } catch (err) {
        toast.error(err.message || "Failed to delete menu.");
      }
    });
  };

  return (
    <Fragment>
      <div className="row">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Menu Management</h4>
              {canAdd && (
                <Link to="/menu-add" className="btn btn-primary btn-sm">
                  <i className="fa fa-plus me-1" /> Add Menu
                </Link>
              )}
            </div>
            <div className="card-body">
              {error && <div className="alert alert-danger">{error}</div>}
              {loading ? (
                <div className="text-center py-4">Loading menus...</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped align-middle">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Key</th>
                        <th>Path</th>
                        <th>Order</th>
                        <th>Status</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-4">
                            No menus found.
                          </td>
                        </tr>
                      ) : (
                        rows.map((menu) => (
                          <tr key={menu._id}>
                            <td>
                              <span style={{ paddingLeft: `${menu.depth * 20}px` }}>
                                {menu.depth > 0 ? "└ " : ""}
                                {menu.name}
                              </span>
                            </td>
                            <td>{menu.key}</td>
                            <td>{menu.path}</td>
                            <td>{menu.order ?? 0}</td>
                            <td>
                              {menu.isActive ? (
                                <span className="badge bg-success">Active</span>
                              ) : (
                                <span className="badge bg-secondary">Inactive</span>
                              )}
                            </td>
                            <td className="text-center">
                              {canUpdate && (
                                <button className="btn btn-primary btn-xs me-1" onClick={() => openEdit(menu)}>
                                  <i className="fa fa-edit" />
                                </button>
                              )}
                              {canDelete && (
                                <button className="btn btn-danger btn-xs" onClick={() => handleDelete(menu)}>
                                  <i className="fa fa-trash" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal show={showEditModal} onHide={closeEdit} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Menu</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingMenu && (
            <MenuForm
              initialValues={{
                name: editingMenu.name,
                key: editingMenu.key,
                path: editingMenu.path,
                icon: editingMenu.icon,
                parentId: editingMenu.parentId || "",
                order: editingMenu.order || 0,
                isActive: editingMenu.isActive !== false,
              }}
              parentOptions={parentOptions.filter((p) => p._id !== editingMenu._id)}
              onSubmit={handleUpdate}
              onCancel={closeEdit}
              submitLabel="Update Menu"
            />
          )}
        </Modal.Body>
      </Modal>
    </Fragment>
  );
}
